<script lang="ts" module>
	import type { Snippet } from 'svelte';
	import type { ClassValue, HTMLAttributes } from 'svelte/elements';
	import { Dialog } from 'bits-ui';
	import { PORTAL_CONTAINER_SELECTOR } from '$lib/constants';
	import { arrowKeyNavigation } from '$lib/actions/arrow-key-navigation.svelte';
	import { overlayStore } from '$lib/stores/overlay.svelte';
	import { layerProps } from '../layer-props';
	import { tv } from '../tv';
	import OverlayBackdrop from './overlay-backdrop.svelte';
	import OverlayCenter from './overlay-center.svelte';

	export const overlayVariants = tv({ base: 'absolute inset-s0 z-max' });

	export interface OverlayProps extends Omit<HTMLAttributes<HTMLElement>, 'class'> {
		arrowNavigation?: boolean;
		backdrop?: boolean | Snippet;
		center?: boolean;
		children: Snippet;
		class?: ClassValue;
		closeOnEscape?: boolean;
		closeOnOutsideClick?: boolean;
		initialFocus?: boolean;
		onRequestClose?: () => void;
		open: boolean;
		ref?: HTMLElement | null;
		returnFocus?: boolean;
	}
</script>

<script lang="ts">
	let {
		arrowNavigation = false,
		backdrop = false,
		center = true,
		children,
		class: className,
		closeOnEscape = true,
		closeOnOutsideClick = true,
		initialFocus = false,
		onRequestClose,
		open,
		ref = $bindable(null),
		returnFocus = true,
		...rest
	}: OverlayProps = $props();

	$effect(() => {
		if (!open) return;
		return overlayStore.acquire();
	});
</script>

{#snippet trap()}
	<Dialog.Content
		preventScroll={false}
		{...layerProps({ closeOnEscape, closeOnOutsideClick, initialFocus, returnFocus })}
	>
		{#snippet child({ props })}
			<div {...props} class="contents" use:arrowKeyNavigation={arrowNavigation}>
				{@render children()}
			</div>
		{/snippet}
	</Dialog.Content>
{/snippet}

<Dialog.Root
	bind:open={
		() => open,
		(next) => {
			if (!next) onRequestClose?.();
		}
	}
>
	<Dialog.Portal to={PORTAL_CONTAINER_SELECTOR}>
		{#if open}
			<div bind:this={ref} class={[overlayVariants(), className]} {...rest}>
				{#if backdrop === true}
					<OverlayBackdrop />
				{:else if backdrop}
					{@render backdrop()}
				{/if}
				{#if center}
					<OverlayCenter>{@render trap()}</OverlayCenter>
				{:else}
					{@render trap()}
				{/if}
			</div>
		{/if}
	</Dialog.Portal>
</Dialog.Root>
